/** Integration catalog + display metadata. Live status comes from /api/integrations. */

/**
 * Logo sources:
 * - Partner PNGs dropped into /public/images (Dynamics, Klaviyo, QuickBooks)
 * - simple-icons SVG exports for global brands (HubSpot, Klarna, etc.)
 * - Wikimedia / crafted SVGs for Nordic brands not in simple-icons
 */
export const INTEGRATION_LOGOS = {
  dynamics: '/images/Dynamics-365-logo.png',
  klaviyo: '/images/Klaviyo-logo.png',
  quickbooks: '/images/QuickBooks-Logo.png',
  economic: '/images/economic-logo.svg',
  visma: '/images/visma-logo.png',
  fortnox: '/images/fortnox-logo.png',
  hubspot: '/images/hubspot-logo.svg',
  mailchimp: '/images/mailchimp-logo.svg',
  voyado: '/images/voyado-logo.svg',
  superoffice: '/images/superoffice-logo.svg',
  mobilepay: '/images/mobilepay-logo.svg',
  nets: '/images/nets-logo.svg',
  klarna: '/images/klarna-logo.svg',
  quickpay: '/images/quickpay-logo.png',
  stripe: '/images/stripe-logo.svg',
  meta: '/images/meta-logo.svg',
  googleads: '/images/googleads-logo.svg',
  googleanalytics: '/images/googleanalytics-logo.svg',
  zendesk: '/images/zendesk-logo.svg',
  intercom: '/images/intercom-logo.svg',
  brevo: '/images/brevo-logo.svg',
  sap: '/images/sap-logo.svg',
};

export const INTEGRATION_CATEGORIES = [
  {
    id: 'erp',
    label: 'ERP & accounting',
    description: 'Finance export for each venue tenant.',
  },
  {
    id: 'marketing',
    label: 'Marketing & CRM',
    description: 'Audience, campaigns, and guest CRM per tenant.',
  },
  {
    id: 'payments',
    label: 'Payments',
    description: 'Nordic checkout and acquiring for tickets and merch.',
  },
  {
    id: 'analytics',
    label: 'Ads & analytics',
    description: 'Attribution into journeys and funnels.',
  },
];

/** Full directory people can search (Slack-style app directory). */
export const INTEGRATION_CATALOG = [
  // — Available on live API —
  {
    id: 'dynamics',
    provider: 'dynamics',
    category: 'erp',
    name: 'Microsoft Dynamics 365',
    shortName: 'Dynamics',
    description: 'Sync invoices into Dynamics with Azure AD, one connection per venue.',
    outcome: 'Unlock invoice export and account mapping.',
    capabilities: ['Invoice export', 'Azure AD'],
    docsHint: 'Needs Azure AD env vars (DYNAMICS_CLIENT_ID / SECRET / REDIRECT_URI).',
    logoKey: 'dynamics',
    logoUrl: INTEGRATION_LOGOS.dynamics,
    available: true,
    keywords: ['dynamics', 'microsoft', 'erp', 'finance', 'azure', 'd365'],
  },
  {
    id: 'klaviyo',
    provider: 'klaviyo',
    category: 'marketing',
    name: 'Klaviyo',
    shortName: 'Klaviyo',
    description: 'Connect each venue’s own Klaviyo account for lists and triggers.',
    outcome: 'Unlock audience sync and event triggers.',
    capabilities: ['Audience sync', 'Events'],
    docsHint: 'Per-venue private API key (OAuth partner program not required).',
    logoKey: 'klaviyo',
    logoUrl: INTEGRATION_LOGOS.klaviyo,
    available: true,
    keywords: ['klaviyo', 'email', 'marketing', 'audience', 'campaign'],
  },

  // — Catalog only (not on /api/integrations yet) —
  {
    id: 'economic',
    provider: 'economic',
    category: 'erp',
    name: 'e-conomic',
    shortName: 'e-conomic',
    description: 'Danish Visma accounting, the default for many Nordic mid-market venues.',
    outcome: 'Unlock invoice and ledger sync for DK tenants.',
    capabilities: ['Invoice export', 'Ledger'],
    docsHint: 'Not on the live integrations API yet.',
    logoKey: 'economic',
    logoUrl: INTEGRATION_LOGOS.economic,
    available: false,
    keywords: ['e-conomic', 'economic', 'visma', 'denmark', 'dk', 'accounting', 'erp', 'bogføring'],
  },
  {
    id: 'hubspot',
    provider: 'hubspot',
    category: 'marketing',
    name: 'HubSpot',
    shortName: 'HubSpot',
    description: 'CRM and marketing hub widely used across Nordic venue sales teams.',
    outcome: 'Unlock contacts, deals, and marketing lists.',
    capabilities: ['CRM', 'Lists'],
    docsHint: 'Not on the live integrations API yet.',
    logoKey: 'hubspot',
    logoUrl: INTEGRATION_LOGOS.hubspot,
    available: false,
    keywords: ['hubspot', 'crm', 'marketing', 'sales', 'nordic'],
  },

  // — Coming soon: Nordic ERP —
  {
    id: 'visma',
    provider: 'visma',
    category: 'erp',
    name: 'Visma.net',
    shortName: 'Visma',
    description: 'Enterprise Nordic finance suite for larger venue groups.',
    outcome: 'Finance sync for Visma.net tenants.',
    capabilities: ['Invoice export'],
    docsHint: 'Roadmap for multi-entity Visma tenants.',
    logoKey: 'visma',
    logoUrl: INTEGRATION_LOGOS.visma,
    available: false,
    keywords: ['visma', 'visma.net', 'erp', 'norway', 'sweden', 'finland', 'nordic'],
  },
  {
    id: 'fortnox',
    provider: 'fortnox',
    category: 'erp',
    name: 'Fortnox',
    shortName: 'Fortnox',
    description: 'Swedish cloud accounting for venues operating in SE.',
    outcome: 'Invoice export for Swedish tenants.',
    capabilities: ['Invoice export'],
    docsHint: 'Pending SE market expansion.',
    logoKey: 'fortnox',
    logoUrl: INTEGRATION_LOGOS.fortnox,
    available: false,
    keywords: ['fortnox', 'sweden', 'se', 'accounting', 'erp'],
  },
  {
    id: 'quickbooks',
    provider: 'quickbooks',
    category: 'erp',
    name: 'QuickBooks Online',
    shortName: 'QuickBooks',
    description: 'Optional ERP for smaller or international venues.',
    outcome: 'Invoice export when product confirms scope.',
    capabilities: ['Invoice export'],
    docsHint: 'Less common in DK than e-conomic; kept as optional.',
    logoKey: 'quickbooks',
    logoUrl: INTEGRATION_LOGOS.quickbooks,
    available: false,
    keywords: ['quickbooks', 'qb', 'intuit', 'accounting', 'erp'],
  },
  {
    id: 'sap',
    provider: 'sap',
    category: 'erp',
    name: 'SAP',
    shortName: 'SAP',
    description: 'Enterprise ERP for large cultural and sports groups.',
    outcome: 'Finance export for SAP-connected tenants.',
    capabilities: ['Invoice export'],
    docsHint: 'Enterprise roadmap only.',
    logoKey: 'sap',
    logoUrl: INTEGRATION_LOGOS.sap,
    available: false,
    keywords: ['sap', 'erp', 'enterprise', 'finance'],
  },

  // — Coming soon: marketing / CRM —
  {
    id: 'mailchimp',
    provider: 'mailchimp',
    category: 'marketing',
    name: 'Mailchimp',
    shortName: 'Mailchimp',
    description: 'Email marketing still used by many smaller Nordic venues.',
    outcome: 'Audience and campaign sync.',
    capabilities: ['Audience sync'],
    docsHint: 'Coming after Klaviyo and HubSpot.',
    logoKey: 'mailchimp',
    logoUrl: INTEGRATION_LOGOS.mailchimp,
    available: false,
    keywords: ['mailchimp', 'email', 'marketing', 'newsletter'],
  },
  {
    id: 'voyado',
    provider: 'voyado',
    category: 'marketing',
    name: 'Voyado',
    shortName: 'Voyado',
    description: 'Nordic CRM and loyalty, strong fit for season cards and members.',
    outcome: 'Loyalty and membership sync.',
    capabilities: ['Loyalty', 'CRM'],
    docsHint: 'Nordic CRM roadmap.',
    logoKey: 'voyado',
    logoUrl: INTEGRATION_LOGOS.voyado,
    available: false,
    keywords: ['voyado', 'loyalty', 'crm', 'membership', 'nordic', 'sweden'],
  },
  {
    id: 'superoffice',
    provider: 'superoffice',
    category: 'marketing',
    name: 'SuperOffice',
    shortName: 'SuperOffice',
    description: 'Classic Nordic B2B CRM for corporate hospitality and sales.',
    outcome: 'Corporate account and pipeline sync.',
    capabilities: ['CRM'],
    docsHint: 'Nordic CRM roadmap.',
    logoKey: 'superoffice',
    logoUrl: INTEGRATION_LOGOS.superoffice,
    available: false,
    keywords: ['superoffice', 'crm', 'b2b', 'norway', 'nordic'],
  },
  {
    id: 'brevo',
    provider: 'brevo',
    category: 'marketing',
    name: 'Brevo',
    shortName: 'Brevo',
    description: 'Email and SMS automation alternative for EU venues.',
    outcome: 'Audience and transactional messaging.',
    capabilities: ['Email', 'SMS'],
    docsHint: 'Secondary marketing option.',
    logoKey: 'brevo',
    logoUrl: INTEGRATION_LOGOS.brevo,
    available: false,
    keywords: ['brevo', 'sendinblue', 'email', 'sms', 'marketing'],
  },
  {
    id: 'zendesk',
    provider: 'zendesk',
    category: 'marketing',
    name: 'Zendesk',
    shortName: 'Zendesk',
    description: 'Guest support tickets linked to venue customers.',
    outcome: 'Support context beside revenue data.',
    capabilities: ['Support'],
    docsHint: 'Ops add-on, not core finance.',
    logoKey: 'zendesk',
    logoUrl: INTEGRATION_LOGOS.zendesk,
    available: false,
    keywords: ['zendesk', 'support', 'helpdesk', 'tickets'],
  },
  {
    id: 'intercom',
    provider: 'intercom',
    category: 'marketing',
    name: 'Intercom',
    shortName: 'Intercom',
    description: 'In-product messaging for digital ticket experiences.',
    outcome: 'Guest messaging and support.',
    capabilities: ['Messaging'],
    docsHint: 'Ops add-on.',
    logoKey: 'intercom',
    logoUrl: INTEGRATION_LOGOS.intercom,
    available: false,
    keywords: ['intercom', 'messaging', 'support', 'chat'],
  },

  // — Coming soon: payments (DK / Nordic) —
  {
    id: 'mobilepay',
    provider: 'mobilepay',
    category: 'payments',
    name: 'MobilePay',
    shortName: 'MobilePay',
    description: 'Denmark’s everyday wallet, expected by guests at checkout.',
    outcome: 'MobilePay payment insights beside ticket revenue.',
    capabilities: ['Checkout'],
    docsHint: 'Payment partner roadmap for DK.',
    logoKey: 'mobilepay',
    logoUrl: INTEGRATION_LOGOS.mobilepay,
    available: false,
    keywords: ['mobilepay', 'denmark', 'dk', 'payments', 'wallet', 'vipps'],
  },
  {
    id: 'nets',
    provider: 'nets',
    category: 'payments',
    name: 'Nets',
    shortName: 'Nets',
    description: 'Nordic card acquiring used across venues and box offices.',
    outcome: 'Acquiring and settlement context.',
    capabilities: ['Card acquiring'],
    docsHint: 'Payment partner roadmap.',
    logoKey: 'nets',
    logoUrl: INTEGRATION_LOGOS.nets,
    available: false,
    keywords: ['nets', 'nexi', 'acquiring', 'cards', 'denmark', 'nordic', 'payments'],
  },
  {
    id: 'klarna',
    provider: 'klarna',
    category: 'payments',
    name: 'Klarna',
    shortName: 'Klarna',
    description: 'Nordic-born pay-later and checkout for web tickets and merch.',
    outcome: 'Klarna checkout attribution.',
    capabilities: ['Checkout'],
    docsHint: 'Payment partner roadmap.',
    logoKey: 'klarna',
    logoUrl: INTEGRATION_LOGOS.klarna,
    available: false,
    keywords: ['klarna', 'payments', 'checkout', 'sweden', 'nordic', 'bnpl'],
  },
  {
    id: 'quickpay',
    provider: 'quickpay',
    category: 'payments',
    name: 'QuickPay',
    shortName: 'QuickPay',
    description: 'Popular Danish payment gateway for web shops and ticketing.',
    outcome: 'Gateway settlement sync.',
    capabilities: ['Gateway'],
    docsHint: 'DK payments roadmap.',
    logoKey: 'quickpay',
    logoUrl: INTEGRATION_LOGOS.quickpay,
    available: false,
    keywords: ['quickpay', 'denmark', 'dk', 'gateway', 'payments'],
  },
  {
    id: 'stripe',
    provider: 'stripe',
    category: 'payments',
    name: 'Stripe',
    shortName: 'Stripe',
    description: 'Global payments, growing for digital and merch checkouts.',
    outcome: 'Stripe payout and charge context.',
    capabilities: ['Checkout'],
    docsHint: 'Secondary to MobilePay and Nets in DK.',
    logoKey: 'stripe',
    logoUrl: INTEGRATION_LOGOS.stripe,
    available: false,
    keywords: ['stripe', 'payments', 'checkout', 'cards'],
  },

  // — Coming soon: ads / analytics —
  {
    id: 'meta',
    provider: 'meta',
    category: 'analytics',
    name: 'Meta Ads',
    shortName: 'Meta Ads',
    description: 'Facebook and Instagram ad spend for journey attribution.',
    outcome: 'Paid social into Paths and Funnel.',
    capabilities: ['Attribution'],
    docsHint: 'Attribution roadmap.',
    logoKey: 'meta',
    logoUrl: INTEGRATION_LOGOS.meta,
    available: false,
    keywords: ['meta', 'facebook', 'instagram', 'ads', 'attribution'],
  },
  {
    id: 'googleads',
    provider: 'googleads',
    category: 'analytics',
    name: 'Google Ads',
    shortName: 'Google Ads',
    description: 'Search and display spend for event demand generation.',
    outcome: 'Paid search into Paths and Funnel.',
    capabilities: ['Attribution'],
    docsHint: 'Attribution roadmap.',
    logoKey: 'googleads',
    logoUrl: INTEGRATION_LOGOS.googleads,
    available: false,
    keywords: ['google', 'ads', 'adwords', 'sem', 'attribution'],
  },
  {
    id: 'googleanalytics',
    provider: 'googleanalytics',
    category: 'analytics',
    name: 'Google Analytics',
    shortName: 'GA4',
    description: 'Web funnel events into Venue Intelligence journeys.',
    outcome: 'Site behaviour beside ticket conversion.',
    capabilities: ['Web analytics'],
    docsHint: 'Analytics roadmap.',
    logoKey: 'googleanalytics',
    logoUrl: INTEGRATION_LOGOS.googleanalytics,
    available: false,
    keywords: ['google', 'analytics', 'ga4', 'web', 'funnel'],
  },
];

function catalogToInstalled(entry) {
  return {
    id: entry.id,
    provider: entry.provider,
    category: entry.category,
    name: entry.name,
    shortName: entry.shortName,
    description: entry.description,
    outcome: entry.outcome,
    status: entry.available ? 'disconnected' : 'coming_soon',
    syncStatus: null,
    lastSyncedAt: null,
    connectedAt: null,
    accountLabel: null,
    errorMessage: null,
    capabilities: entry.capabilities,
    docsHint: entry.docsHint,
    logoKey: entry.logoKey,
    logoUrl: entry.logoUrl,
    available: entry.available,
  };
}

/** Legacy empty mock — live status comes from GET /api/integrations. */
export const INTEGRATIONS_MOCK = {
  dataSourceLabel: 'Venue Intelligence',
  updatedAt: null,
  items: [],
};

export function buildInstalledFromCatalog(provider) {
  const entry = INTEGRATION_CATALOG.find(
    (item) => item.provider === provider || item.id === provider
  );
  if (!entry) return null;
  return catalogToInstalled(entry);
}

export function searchIntegrationCatalog(query = '') {
  const q = String(query).trim().toLowerCase();
  if (!q) return INTEGRATION_CATALOG;
  return INTEGRATION_CATALOG.filter((item) => {
    const haystack = [
      item.name,
      item.shortName,
      item.description,
      item.category,
      ...(item.keywords || []),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}
